/**
 * Chart Editors Registry
 *
 * Wraps all chart editors as PropertyEditor objects for the registry
 */

import React from 'react';
import type { PropertyEditor, PropertyEditorProps } from '../../../../registry/PropertyEditor.types';
import type { FormControl } from '@atlas-forms/types-js';
import { BarChartEditor } from './BarChartEditor';
import { LineChartEditor } from './LineChartEditor';
import { PieChartEditor } from './PieChartEditor';
import { AreaChartEditor } from './AreaChartEditor';
import { ScatterPlotEditor } from './ScatterPlotEditor';
import { BubbleChartEditor } from './BubbleChartEditor';
import { HistogramEditor } from './HistogramEditor';
import { WaterfallChartEditor } from './WaterfallChartEditor';
import { TreeMapEditor } from './TreeMapEditor';
import { HeatmapEditor } from './HeatmapEditor';
import { SankeyDiagramEditor } from './SankeyDiagramEditor';
import { NetworkGraphEditor } from './NetworkGraphEditor';
import { GanttChartEditor } from './GanttChartEditor';

/**
 * Factory function to create a PropertyEditor from a chart editor component
 */
function createChartPropertyEditor(
  type: string,
  label: string,
  ChartEditorComponent: React.FC<any>,
  description?: string,
): PropertyEditor {
  return {
    type,
    label,
    description,
    render: (props: PropertyEditorProps) => {
      const { control, onControlChange } = props;
      const config = control.config ?? {};

      return (
        <ChartEditorComponent
          config={config}
          onChange={(newConfig: any) => {
            onControlChange({ ...control, config: { ...config, ...newConfig } });
          }}
        />
      );
    },
  };
}

// Export all chart editors wrapped as PropertyEditor objects
export const BarChartPropertyEditor = createChartPropertyEditor(
  'bar-chart',
  'Bar Chart',
  BarChartEditor,
  'Configure bar chart visualization',
);

export const LineChartPropertyEditor = createChartPropertyEditor(
  'line-chart',
  'Line Chart',
  LineChartEditor,
  'Configure line chart visualization',
);

export const PieChartPropertyEditor = createChartPropertyEditor(
  'pie-chart',
  'Pie Chart',
  PieChartEditor,
  'Configure pie/donut chart visualization',
);

export const AreaChartPropertyEditor = createChartPropertyEditor(
  'area-chart',
  'Area Chart',
  AreaChartEditor,
  'Configure area chart visualization',
);

export const ScatterPlotPropertyEditor = createChartPropertyEditor(
  'scatter-plot',
  'Scatter Plot',
  ScatterPlotEditor,
  'Configure scatter plot visualization',
);

export const BubbleChartPropertyEditor = createChartPropertyEditor(
  'bubble-chart',
  'Bubble Chart',
  BubbleChartEditor,
  'Configure bubble chart visualization',
);

export const HistogramPropertyEditor = createChartPropertyEditor(
  'histogram',
  'Histogram',
  HistogramEditor,
  'Configure histogram visualization',
);

export const WaterfallChartPropertyEditor = createChartPropertyEditor(
  'waterfall-chart',
  'Waterfall Chart',
  WaterfallChartEditor,
  'Configure waterfall chart visualization',
);

export const TreeMapPropertyEditor = createChartPropertyEditor(
  'tree-map',
  'Tree Map',
  TreeMapEditor,
  'Configure tree map visualization',
);

export const HeatmapPropertyEditor = createChartPropertyEditor(
  'heatmap',
  'Heatmap',
  HeatmapEditor,
  'Configure heatmap visualization',
);

export const SankeyDiagramPropertyEditor = createChartPropertyEditor(
  'sankey-diagram',
  'Sankey Diagram',
  SankeyDiagramEditor,
  'Configure sankey diagram visualization',
);

export const NetworkGraphPropertyEditor = createChartPropertyEditor(
  'network-graph',
  'Network Graph',
  NetworkGraphEditor,
  'Configure network graph visualization',
);

export const GanttChartPropertyEditor = createChartPropertyEditor(
  'gantt-chart',
  'Gantt Chart',
  GanttChartEditor,
  'Configure gantt chart visualization',
);
